import { Audiogram } from '@zealous/template/Audiogram'
import { LAYOUTS, Layout } from '@zealous/template/layouts'
import { SCENES, SceneKind } from '@zealous/template/scenes'
import { ClipScene } from '@zealous/template/scenes/ClipScene'
import {
  AudiogramConfig,
  FPS,
  ORIENTATIONS,
  Orientation,
  audiogramPropsSchema,
  clipSceneDurationInFrames,
  clipScenePropsSchema,
  scenePropsSchema,
} from '@zealous/template/types'
import { defaultClipScene } from '@zealous/template/types/defaultClipScene'
import { defaultWrapConfig } from '@zealous/template/types/defaultWrap'
import { THEMES } from '@zealous/template/types/themes'
import applyTheme from '@zealous/template/utils/applyTheme'
import {
  TIMELINE_COMPOSITION_ID,
  TimelineComposition,
  calculateTimelineMetadata,
} from '@zealous/template/timeline/TimelineComposition'
import { defaultTimelineDoc } from '@zealous/template/timeline/defaultDoc'
import { timelineDocSchema } from '@zealous/template/timeline/types'
import { scenesDoc, twoAvatarsDoc, twoHeadsDoc } from './pinnedDocs'
import { v2Doc } from './templateDoc'
import sumBy from 'lodash/sumBy'
import { Composition, Folder, getInputProps } from 'remotion'

import './style.css'

export function RemotionStudio() {
  const inputProps = getInputProps<{ config?: AudiogramConfig }>()
  const scenes = inputProps.config?.scenes
  const durationInFrames = scenes?.length
    ? Math.round(FPS * sumBy(scenes, (s) => s.duration))
    : undefined

  return (
    <>
      {/* Every v2 render (#77): one composition, the hydrated document as
          props. Length, size and rate come from `calculateMetadata`. */}
      <Composition
        id={TIMELINE_COMPOSITION_ID}
        component={TimelineComposition}
        schema={timelineDocSchema}
        calculateMetadata={calculateTimelineMetadata}
        durationInFrames={30 * FPS}
        fps={FPS}
        width={defaultTimelineDoc.compositionWidth}
        height={defaultTimelineDoc.compositionHeight}
        defaultProps={defaultTimelineDoc}
      />
      {/* Every v2 template over the demo recording (#546): what a layout's
          blueprint produces, for tuning next to its v1 composition below.
          `bun compare:layouts` renders the same pairs to files. */}
      <Folder name="templates">
        {(Object.keys(LAYOUTS) as Layout[]).map((layout) => (
          <Folder key={layout} name={layout}>
            {(Object.keys(ORIENTATIONS) as Orientation[]).map((orientation) => (
              <Composition
                key={orientation}
                id={`template-${layout}-${orientation}`}
                component={TimelineComposition}
                schema={timelineDocSchema}
                calculateMetadata={calculateTimelineMetadata}
                durationInFrames={30 * FPS}
                fps={FPS}
                width={ORIENTATIONS[orientation].width}
                height={ORIENTATIONS[orientation].height}
                defaultProps={v2Doc(layout, orientation)}
              />
            ))}
          </Folder>
        ))}
      </Folder>
      {/* Speaker pinning (#566): two generation-3 documents whose speaker
          elements are each pinned to one participant; scrub across a turn
          and through the gap to see the active one lit and both dim. */}
      <Folder name="speaker-pins">
        {(
          [
            ['pin-two-heads', twoHeadsDoc],
            ['pin-two-avatars', twoAvatarsDoc],
            ['scenes-two-views-blank', scenesDoc],
          ] as const
        ).map(([id, build]) => (
          <Composition
            key={id}
            id={id}
            component={TimelineComposition}
            schema={timelineDocSchema}
            calculateMetadata={calculateTimelineMetadata}
            durationInFrames={30 * FPS}
            fps={FPS}
            width={1080}
            height={1920}
            defaultProps={build()}
          />
        ))}
      </Folder>
      <Folder name="scenes">
        {(Object.keys(SCENES) as SceneKind[]).map((sceneKind) =>
          sceneKind === 'clip' ? (
            <Folder key="clip" name="clips">
              {(Object.keys(LAYOUTS) as Layout[]).map((layout) => (
                <Folder key={layout} name={layout}>
                  {(Object.keys(ORIENTATIONS) as Orientation[]).map(
                    (orientation) => {
                      const { defaultScene, defaultStyle, transcriptions } =
                        LAYOUTS[layout]

                      const baseScene = {
                        ...defaultClipScene,
                        ...defaultScene,
                        layout,
                      }
                      const theme = baseScene.theme ?? THEMES[0]!.config
                      const style = applyTheme(defaultStyle, theme)

                      const clipSceneConfig = {
                        ...baseScene,
                        layout,
                        style: {
                          ...style,
                          transcription: {
                            ...style.transcription,
                            ...transcriptions[orientation],
                          },
                        },
                      }

                      return (
                        <Composition
                          id={`${layout}-${orientation}`}
                          component={ClipScene}
                          // The registered length is only the Studio preview
                          // default. Every real render passes its own
                          // `clipScene.duration` in `inputProps`, and before
                          // this hook Lambda ignored it and rendered the
                          // layout default — so every clip render came out 30 s
                          // (or 6 s) long regardless of what was asked for.
                          calculateMetadata={({ props }) => ({
                            durationInFrames: clipSceneDurationInFrames(
                              props.clipScene.duration,
                            ),
                          })}
                          durationInFrames={
                            durationInFrames ??
                            clipSceneDurationInFrames(clipSceneConfig.duration)
                          }
                          key={orientation}
                          fps={FPS}
                          width={ORIENTATIONS[orientation].width}
                          height={ORIENTATIONS[orientation].height}
                          schema={clipScenePropsSchema}
                          defaultProps={{
                            clipScene: clipSceneConfig,
                            orientation,
                          }}
                        />
                      )
                    },
                  )}
                </Folder>
              ))}
            </Folder>
          ) : (
            <Folder key={sceneKind} name={sceneKind}>
              {(Object.keys(ORIENTATIONS) as Orientation[]).map(
                (orientation) => {
                  const SceneComponent = SCENES[sceneKind]

                  return (
                    <Composition
                      key={orientation}
                      id={`${sceneKind}-${orientation}`}
                      component={SceneComponent}
                      durationInFrames={
                        durationInFrames ??
                        Math.round(
                          FPS *
                            sumBy(defaultWrapConfig.scenes, (s) => s.duration),
                        )
                      }
                      fps={FPS}
                      width={ORIENTATIONS[orientation].width}
                      height={ORIENTATIONS[orientation].height}
                      schema={scenePropsSchema}
                      defaultProps={{
                        audiogram: defaultWrapConfig,
                        orientation,
                        scene: defaultWrapConfig.scenes![0]!,
                      }}
                    />
                  )
                },
              )}
            </Folder>
          ),
        )}
      </Folder>

      {/* <Folder name="wraps">
        {(Object.keys(ORIENTATIONS) as Orientation[]).map((orientation) => (
          <Composition
            key={orientation}
            id={`wrap-${orientation}`}
            component={Audiogram}
            durationInFrames={
              durationInFrames ??
              Math.round(
                sumBy(defaultWrapConfig.scenes, (s) => s.duration) * FPS,
              )
            }
            fps={FPS}
            width={ORIENTATIONS[orientation].width}
            height={ORIENTATIONS[orientation].height}
            schema={audiogramPropsSchema}
            defaultProps={{ config: defaultWrapConfig, orientation }}
          />
        ))}
      </Folder> */}
    </>
  )
}
